import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { keyframes } from '@mui/system';

/**
 * Dashboard load sequence (one orchestrated reveal per tab load): cards rise in order, numbers count up, charts and
 * sparklines draw in, list rows follow. Skipped under "reduce motion" and in tests, where values show at once.
 */

const rise = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: none; }
`;
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: none; }
`;
const grow = keyframes`
  from { transform: scaleX(0); }
  to { transform: scaleX(1); }
`;
const draw = keyframes`
  from { stroke-dashoffset: 1; }
  to { stroke-dashoffset: 0; }
`;
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const EASE = 'cubic-bezier(.2,.7,.2,1)';
const CARD_STAGGER = 60;
const MAX_CARD_DELAY = 540;

export const motionOk = () => {
  if (process.env.NODE_ENV === 'test' || typeof window === 'undefined') return false;
  return !(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
};

const RevealContext = createContext(null);

/** Wraps one tab load; remount it (key) to play the sequence again. Cards take their turn in render order. */
export const RevealGroup = ({ children }) => {
  const next = useRef(0);
  return <RevealContext.Provider value={next}>{children}</RevealContext.Provider>;
};

/** The card's entrance (opacity + 8px rise), delayed by its place in the group. */
export const useCardReveal = () => {
  const next = useContext(RevealContext);
  const [delay] = useState(() => {
    if (!next) return null;
    const d = Math.min(next.current * CARD_STAGGER, MAX_CARD_DELAY);
    next.current += 1;
    return d;
  });
  if (delay == null || !motionOk()) return {};
  return { animation: `${rise} 360ms ${EASE} both`, animationDelay: `${delay}ms` };
};

/** A list row inside a card: a small fade-up after the card has started. */
export const rowReveal = (index) => (motionOk()
  ? { animation: `${fadeUp} 260ms ${EASE} both`, animationDelay: `${220 + Math.min(index, 8) * 40}ms` }
  : {});

/** A stacked bar that grows from the left. */
export const growReveal = (delay = 200) => (motionOk()
  ? { transformOrigin: 'left center', animation: `${grow} 700ms ${EASE} both`, animationDelay: `${delay}ms` }
  : {});

/** An SVG path drawn from start to end (needs pathLength="1" and stroke-dasharray 1). */
export const drawReveal = (delay = 250) => (motionOk()
  ? { strokeDasharray: 1, strokeDashoffset: 0, animation: `${draw} 800ms ${EASE} both`, animationDelay: `${delay}ms` }
  : {});

export const fadeReveal = (delay = 0, duration = 400) => (motionOk()
  ? { animation: `${fadeIn} ${duration}ms ease-out both`, animationDelay: `${delay}ms` }
  : {});

const easeOut = (t) => 1 - Math.pow(1 - t, 3);

/** A number moving to `target` (from 0 the first time, from the last value after that). */
export const useCountUp = (target, duration = 800) => {
  const animate = motionOk() && Number.isFinite(target);
  const [shown, setShown] = useState(animate ? 0 : target);
  const from = useRef(animate ? 0 : target);
  useEffect(() => {
    if (!animate) {
      setShown(target);
      from.current = target;
      return undefined;
    }
    const start = Number.isFinite(from.current) ? from.current : 0;
    const began = performance.now();
    let frame;
    const tick = (now) => {
      const t = Math.min(1, (now - began) / duration);
      const v = start + (target - start) * easeOut(t);
      setShown(v);
      from.current = v;
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, duration, animate]);
  return Number.isFinite(shown) ? shown : 0;
};

const NUMBER = /-?\d[\d,]*(\.\d+)?/;

/**
 * Counts a displayed value up: the first number in a string ("AED 12,500.00", "−1,250", "38%") moves while the text
 * around it and its decimals stay as they are. Anything without a number (a node, "—") shows as is.
 */
export const AnimatedNumber = ({ value }) => {
  const text = typeof value === 'number' ? String(value) : (typeof value === 'string' ? value : null);
  const match = text ? text.match(NUMBER) : null;
  const target = match ? Number(match[0].replace(/,/g, '')) : NaN;
  const shown = useCountUp(match ? target : NaN);
  if (!match) return value ?? null;
  const decimals = match[1] ? match[1].length - 1 : 0;
  const grouped = match[0].includes(',');
  const formatted = Number(shown).toLocaleString('en-US', {
    minimumFractionDigits: decimals, maximumFractionDigits: decimals, useGrouping: grouped,
  });
  return (
    <>
      {text.slice(0, match.index)}
      {formatted}
      {text.slice(match.index + match[0].length)}
    </>
  );
};
